// FeatureFlag.context.tsx
import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import {
  useDecision,
  OptimizelyDecision,
  OptimizelyProvider,
  createInstance,
} from '@optimizely/react-sdk';
import environment from 'app/environment';

// Define feature flag keys
enum FeatureFlagKey {
  CHANGE_APPROVALS = 'fx_change_approvals',
  CHANGE_APPROVALS_SCHEDULES = 'fx_ca_schedules',
  COMPOUND_METRICS = 'compound_metrics',
  FLAGS_FLEX_DASHBOARD = 'flags_flex_dashboard',
  FLAGS_MULTIPLE_EXPERIMENTS = 'flags_multiple_experiments',
  FLAGS_VARIABLES_AI = 'flags_variables_ai',
  FLAGS_VARIATIONS_AI = 'flags_variations_ai',
  FLUTTER_SDK_LIVE = 'flutter_sdk_live',
  FX_CMAB_FLAG = 'fx-cmab-flag',
  FX_FIXED_HORIZON_ENGINE = 'fx_fixed_horizon_engine',
  FX_FLAGS_DASHBOARD_FLEXIBLE_COLUMNS = 'fx_flags_dashboard_flexible_columns',
  FX_VARIABLE_PICKLISTS = 'fx_variable_picklist',
  GRANULAR_PERMISSIONS = 'granular_permissions',
  HOLDOUTS = 'holdouts',
  METRICS_HUB = 'metrics_hub',
  OPTIBOT_CHASSIS = 'optibot_chassis',
  RULE_STATUS = 'fx_rule_status',
  WAREHOUSE_NATIVE_EXPERIMENTATION = 'warehouse_native_experimentation',
  // Add other feature flags here
}

// Interface for a single feature flag
interface FeatureFlag {
  decision: OptimizelyDecision;
  enabled: boolean;
  ready: boolean;
}

// Interface for all feature flags
interface FeatureFlagContextType {
  changeApprovals: FeatureFlag;
  changeApprovalsSchedules: FeatureFlag;
  cmab: FeatureFlag;
  compoundMetrics: FeatureFlag;
  fixedHorizonEngine: FeatureFlag;
  flagsDashboardFlexibleColumns: FeatureFlag;
  flagsFlexDashboard: FeatureFlag;
  flagsMultipleExperiments: FeatureFlag;
  flagsVariablesAI: FeatureFlag;
  flagsVariationsAI: FeatureFlag;
  flutterSdkLive: FeatureFlag;
  fxVariablePicklists: FeatureFlag;
  granularPermissions: FeatureFlag;
  holdouts: FeatureFlag;
  metricsHub: FeatureFlag;
  optibotChassis: FeatureFlag;
  ruleStatus: FeatureFlag;
  warehouseNativeExperimentation: FeatureFlag;
  // Add other feature flags here
}

// Provider props
interface FeatureFlagProviderProps {
  children: ReactNode;
  forceEnableAllFlags?: boolean;
}

// Create context with default values
const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

// to make it mockable for tests
export const isProd = (): boolean =>
  environment.optimizelyEnvironment === 'prod';

// Helper hook to use decision and provide consistent structure
const useFeatureFlag = (
  flagKey: FeatureFlagKey,
  forceEnabled: boolean
): FeatureFlag => {
  const [decision, ready] = useDecision(flagKey, { autoUpdate: true });
  return {
    decision,
    enabled: forceEnabled && !isProd() ? true : ready && decision.enabled,
    ready,
  };
};

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
  forceEnableAllFlags = false,
}) => {
  // Create the Optimizely instance
  const optimizely = createInstance({ sdkKey: environment.optimizelySDKKey });

  // Get account info from the window object
  // @ts-ignore
  const accountInfo = window?.optlyConfig?.account_info || {};

  // Get project ID from context if available
  // @ts-ignore
  const projectId = window?.optlyConfig?.current_project?.id || null;

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      timeout={500}
      user={{
        attributes: {
          account_id: accountInfo.account_id,
          has_optimizely_email: (accountInfo.email || '').includes(
            '@optimizely.com'
          ),
          project_id: projectId,
        },
        id: accountInfo.unique_user_id,
      }}>
      <InnerFeatureFlagProvider forceEnableAllFlags={forceEnableAllFlags}>
        {children}
      </InnerFeatureFlagProvider>
    </OptimizelyProvider>
  );
};

// Inner provider that uses the OptimizelyProvider context
const InnerFeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
  forceEnableAllFlags = false,
}) => {
  // Create hooks for each feature flag
  const changeApprovals = useFeatureFlag(
    FeatureFlagKey.CHANGE_APPROVALS,
    forceEnableAllFlags
  );
  const changeApprovalsSchedules = useFeatureFlag(
    FeatureFlagKey.CHANGE_APPROVALS_SCHEDULES,
    forceEnableAllFlags
  );

  const cmab = useFeatureFlag(FeatureFlagKey.FX_CMAB_FLAG, forceEnableAllFlags);
  const ruleStatus = useFeatureFlag(
    FeatureFlagKey.RULE_STATUS,
    forceEnableAllFlags
  );
  const optibotChassis = useFeatureFlag(
    FeatureFlagKey.OPTIBOT_CHASSIS,
    forceEnableAllFlags
  );
  const flagsVariationsAI = useFeatureFlag(
    FeatureFlagKey.FLAGS_VARIATIONS_AI,
    forceEnableAllFlags
  );
  const holdouts = useFeatureFlag(FeatureFlagKey.HOLDOUTS, forceEnableAllFlags);
  const granularPermissions = useFeatureFlag(
    FeatureFlagKey.GRANULAR_PERMISSIONS,
    forceEnableAllFlags
  );
  const flutterSdkLive = useFeatureFlag(
    FeatureFlagKey.FLUTTER_SDK_LIVE,
    forceEnableAllFlags
  );
  const metricsHub = useFeatureFlag(
    FeatureFlagKey.METRICS_HUB,
    forceEnableAllFlags
  );
  const warehouseNativeExperimentation = useFeatureFlag(
    FeatureFlagKey.WAREHOUSE_NATIVE_EXPERIMENTATION,
    forceEnableAllFlags
  );
  const flagsFlexDashboard = useFeatureFlag(
    FeatureFlagKey.FLAGS_FLEX_DASHBOARD,
    forceEnableAllFlags
  );
  const flagsDashboardFlexibleColumns = useFeatureFlag(
    FeatureFlagKey.FX_FLAGS_DASHBOARD_FLEXIBLE_COLUMNS,
    forceEnableAllFlags
  );
  const flagsVariablesAI = useFeatureFlag(
    FeatureFlagKey.FLAGS_VARIABLES_AI,
    forceEnableAllFlags
  );
  const compoundMetrics = useFeatureFlag(
    FeatureFlagKey.COMPOUND_METRICS,
    forceEnableAllFlags
  );
  const flagsMultipleExperiments = useFeatureFlag(
    FeatureFlagKey.FLAGS_MULTIPLE_EXPERIMENTS,
    forceEnableAllFlags
  );
  const fixedHorizonEngine = useFeatureFlag(
    FeatureFlagKey.FX_FIXED_HORIZON_ENGINE,
    forceEnableAllFlags
  );
  const fxVariablePicklists = useFeatureFlag(
    FeatureFlagKey.FX_VARIABLE_PICKLISTS,
    forceEnableAllFlags
  );

  // Combine all flags into a single value object
  const featureFlags = useMemo<FeatureFlagContextType>(
    () => ({
      changeApprovals,
      changeApprovalsSchedules,
      cmab,
      compoundMetrics,
      fixedHorizonEngine,
      flagsDashboardFlexibleColumns,
      flagsFlexDashboard,
      flagsMultipleExperiments,
      flagsVariablesAI,
      flagsVariationsAI,
      flutterSdkLive,
      fxVariablePicklists,
      granularPermissions,
      holdouts,
      metricsHub,
      optibotChassis,
      ruleStatus,
      warehouseNativeExperimentation,
      // Add more flags here
    }),
    [
      changeApprovals,
      changeApprovalsSchedules,
      cmab,
      compoundMetrics,
      flagsFlexDashboard,
      flagsMultipleExperiments,
      flagsVariablesAI,
      flagsVariationsAI,
      fixedHorizonEngine,
      flutterSdkLive,
      flagsDashboardFlexibleColumns,
      fxVariablePicklists,
      granularPermissions,
      holdouts,
      metricsHub,
      optibotChassis,
      ruleStatus,
      warehouseNativeExperimentation,
    ]
  );

  return (
    <FeatureFlagContext.Provider value={featureFlags}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Create a custom hook for consuming the context
export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagProvider'
    );
  }
  return context;
};

// Individual hooks for specific flags
export const useChangeApprovalsFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.changeApprovals;
};

export const useChangeApprovalsSchedulesFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.changeApprovalsSchedules;
};

export const useCMABFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.cmab;
};

export const useRuleStatusFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.ruleStatus;
};

export const useOptibotChassisFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.optibotChassis;
};

export const useFlagsVariationsAIFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.flagsVariationsAI;
};

export const useHoldoutsFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.holdouts;
};

// New individual hooks for the added feature flags
export const useGranularPermissionsFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.granularPermissions;
};

export const useFlutterSdkLiveFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.flutterSdkLive;
};

export const useMetricsHubFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.metricsHub;
};

export const useWarehouseNativeExperimentationFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.warehouseNativeExperimentation;
};

export const useFlagsFlexDashboardFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.flagsFlexDashboard;
};

export const useFlagsDashboardFlexibleColumnsFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.flagsDashboardFlexibleColumns;
};

export const useFlagsVariablesAIFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.flagsVariablesAI;
};

export const useCompoundMetricsFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.compoundMetrics;
};

export const useFlagsMultipleExperimentsFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.flagsMultipleExperiments;
};

export const useFixedHorizonEngineFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.fixedHorizonEngine;
};

export const useVariablePicklistsFlag = (): FeatureFlag => {
  const flags = useFeatureFlags();
  return flags.fxVariablePicklists;
};

// Export the enum for use elsewhere if needed
export { FeatureFlagKey };
